/**
 * Common Composable
 * 管理通用功能（公告、廣播、設定等）
 *
 * 架構：
 * - 狀態管理：使用 Pinia store (stores/core/common.js)
 * - API：使用 useFetch 支援 SSR，透過 onResponse 同步處理資料
 * - 單例模式：使用 nuxtApp._commonContext 避免 storeToRefs 重複創建
 */

import { storeToRefs } from 'pinia'
import { UAParser } from 'ua-parser-js'
import { ref, watch, onBeforeUnmount, getCurrentInstance } from 'vue'

import { useFetch, useRuntimeConfig, useNuxtApp } from '#app'
import { commonApi } from '@/api/common'
import { useAuthToken } from '@/composables/auth/useAuthToken'
import { useSite } from '@/composables/core/useSite'
import { useCommonStore } from '@/stores/core/common'
import { storage } from '@/utils/storage'

const FEED_POLL_INTERVAL = 20000 // 20 seconds
const FBC_COOKIE_MAX_AGE = 60 * 60 * 24 * 7 // 7 days
const MAX_BROADCAST_HISTORY = 100 // 最多保留 100 條廣播記錄

export const useCommon = () => {
  const nuxtApp = useNuxtApp()

  // 單例模式：確保 useFetch 和 storeToRefs 只創建一次
  if (!nuxtApp._commonContext) {
    const commonStore = useCommonStore()
    const { $request } = nuxtApp
    const config = useRuntimeConfig()
    const { apiUrl, clientId, appSecret, intranet } = config.public
    const { getSiteConfig } = useSite()

    // storeToRefs 只調用一次
    const storeRefs = storeToRefs(commonStore)

    // SSR-safe: 非共享狀態
    const userAgent = ref('')
    const isMobile = ref(false)
    const normalBroadcast = ref(null)
    const sentNormalBroadcastList = ref([])
    const isPlaying = ref(false)
    const isLogin = ref(false)
    const pix = ref('')
    const kpix = ref('')

    // Timer context
    const ctx = {
      feedPollTimer: null,
      broadcastTimer: null,
    }

    // Watch stop 函數陣列，用於 cleanup 時停止所有 watch
    const stopWatchers = []

    // 根據環境決定 API base URL
    const baseUrl = import.meta.server && intranet === '1' ? 'http://frontend-api-v2.default.svc.cluster.local' : apiUrl

    // 使用 useFetch 自動取得公告（只創建一次）
    const announcementsApiConfig = commonApi.getAnnouncements({ appSecret })
    const announcementsApiPath = announcementsApiConfig.url.replace(':clientId', clientId)

    // 使用 useFetch 自動執行（SSR 友善）
    // 使用 onResponse 確保 SSR 階段資料同步處理，避免 Hydration 時序問題
    useFetch(`${baseUrl}/${announcementsApiPath}`, {
      key: 'announcements',
      dedupe: 'defer',
      query: announcementsApiConfig.params,
      getCachedData(key, nuxtApp) {
        return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
      },
      onResponse({ response }) {
        if (response._data?.data) {
          commonStore.setAnnouncementList(response._data.data)
        }
      },
      onResponseError({ error: err }) {
        console.error('[useCommon] fetchAnnouncements API error:', err)
      },
    })

    const setUA = () => {
      const parser = new UAParser()
      const result = parser.getResult()
      userAgent.value = result
      isMobile.value = !!result?.device?.type
    }

    const setPixel = () => {
      if (typeof window === 'undefined') return
      const pixValue = storage.get('pix') || ''
      if (pixValue !== '') {
        pix.value = pixValue
      }
    }

    const setKPixel = () => {
      if (typeof window === 'undefined') return
      const kpixValue = storage.get('kpix') || ''
      if (kpixValue !== '') {
        kpix.value = kpixValue
      }
    }

    const fetchConfig = async () => {
      try {
        setUA()
        setPixel()
        setKPixel()
        setFbclid()
        syncFbpToLocalStorage()
      } catch (error) {
        console.error('[useCommon] fetchConfig error:', error)
        throw error
      }
    }

    /**
     * Set Facebook Click ID (_fbc cookie)
     */
    const setFbclid = () => {
      // SSR 環境跳過
      if (typeof window === 'undefined') {
        return
      }

      const { params: urlParams } = useUrlParams(['fbclid'])
      // 使用 watch 來監聽 urlParams 的變化
      // 因為 useUrlParams 是在 onMounted 後才會有值
      stopWatchers.push(
        watch(urlParams, (newParams) => {
          if (newParams?.fbclid) {
            const creationTime = Date.now()
            const fbcValue = `fb.1.${creationTime}.${newParams.fbclid}`
            const fbcCookie = useCookie('_fbc', {
              maxAge: FBC_COOKIE_MAX_AGE,
              sameSite: 'lax',
            })
            fbcCookie.value = fbcValue
            storage.set('_fbc', fbcValue, { maxAge: FBC_COOKIE_MAX_AGE })
          }
        })
      )
    }

    const syncFbpToLocalStorage = () => {
      if (typeof window === 'undefined') return
      const fbpCookie = useCookie('_fbp')
      if (fbpCookie.value) {
        storage.set('_fbp', fbpCookie.value, { maxAge: 60 * 60 * 24 * 90 })
      }
    }

    const getPixelDiagnostic = ({ clientId, eventId, event, fbc, fbp }) => {
      const fbcCookieVal = useCookie('_fbc').value
      const fbpCookieVal = useCookie('_fbp').value
      const fbcLocalVal = storage.get('_fbc')
      const fbpLocalVal = storage.get('_fbp')

      const hasFbq = typeof window.fbq === 'function'
      const fbqInited = hasFbq && !!window.fbq.version
      const cookieEnabled = navigator.cookieEnabled
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
      const isITP = isSafari
      const fbqQueued = Array.isArray(window.fbq?.queue) ? window.fbq.queue.length : -1
      const fbSdkEntry = performance.getEntriesByType('resource').find((e) => e.name.includes('fbevents.js'))
      const fbSdkBlocked = !fbSdkEntry || fbSdkEntry.duration === 0
      const hasFbp = !!fbp
      const hasFbc = !!fbc

      const getFbSource = (cookieVal, localVal, finalVal) => {
        if (!finalVal) return 'none'
        if (cookieVal) return 'cookie'
        if (localVal) return 'localStorage'
        return 'none'
      }

      const isStandalone = ('standalone' in navigator && navigator.standalone) || window.matchMedia('(display-mode: standalone)').matches

      // fbpStatus 分類：當 _fbp cookie 不存在時,依據下列訊號判斷根因
      // - cookie_disabled:  瀏覽器整體停用 cookie(navigator.cookieEnabled = false),_fbp 無法寫入
      // - fbq_missing:      window.fbq 根本不存在,代表 Pixel SDK 從未被注入(plugin 未執行或被移除)
      // - sdk_blocked:      fbevents.js 未出現在 performance resource 清單,或 duration = 0
      //                     表示被 ad blocker / DNS / CSP 攔截,script 沒真正下載完成
      // - fbq_pending:      fbq.queue 有未 flush 的項目(length > 0),代表 fbq('init')/track 有被呼叫
      //                     但 fbevents.js 未載入成功,callMethod 沒綁上,事件全積在 stub queue 裡
      //                     與 sdk_blocked 可互相佐證(前者看 fbq 內部、後者看 performance 外部)
      // - fbq_not_inited:   fbq 存在但 window.fbq.version 為空,代表 stub 載入但 fbq('init') 未跑
      //                     常見於 GTM 先注入 stub、pixelId 卻為 null 的情境
      // - no_pixel_id:      SDK 已載入且初始化,但 pixelId 為空(站台未設定 pixel / GTM 未配置)
      //                     fbq('init', null) 不會產生 _fbp,與 cookie 攔截無關
      // - itp_restricted:   Safari ITP 會將 script 寫入的第一方 cookie 限制為 7 天且可能直接封鎖
      //                     在前述條件都不符時,Safari 仍拿不到 _fbp 多半屬於此類
      // - cookie_blocked:   以上都不是,推測是其他 cookie 層攔截(企業 proxy、隱私擴充、使用者手動刪)
      const hasPixelId = !!nuxtApp.$fb?.options?.pixelId || Object.keys(window?.fbq?.getState?.()?.pixels || {}).length > 0
      let fbpStatus = 'ok'
      if (!cookieEnabled) fbpStatus = 'cookie_disabled'
      else if (!hasFbq) fbpStatus = 'fbq_missing'
      else if (fbSdkBlocked) fbpStatus = 'sdk_blocked'
      else if (fbqQueued > 0) fbpStatus = 'fbq_pending'
      else if (!fbqInited) fbpStatus = 'fbq_not_inited'
      else if (!hasPixelId) fbpStatus = 'no_pixel_id'
      else if (isITP) fbpStatus = 'itp_restricted'
      else fbpStatus = 'cookie_blocked'

      return {
        clientId,
        event,
        eventId,
        hasFbc,
        hasFbp,
        fbcSource: getFbSource(fbcCookieVal, fbcLocalVal, fbc),
        fbpSource: getFbSource(fbpCookieVal, fbpLocalVal, fbp),
        hasAuth: !!appSecret,
        isStandalone,
        fbpStatus,
      }
    }

    /**
     * Send pixel
     */
    const fetchSendPixel = async ({ eventId, event }) => {
      const eventSourceUrl = window.location.href
      const fbcCookie = useCookie('_fbc')
      const fbpCookie = useCookie('_fbp')
      const fbc = fbcCookie.value || storage.get('_fbc') || ''
      const fbp = fbpCookie.value || storage.get('_fbp') || ''
      if (fbcCookie.value) storage.set('_fbc', fbcCookie.value, { maxAge: FBC_COOKIE_MAX_AGE })
      if (fbpCookie.value) storage.set('_fbp', fbpCookie.value, { maxAge: 60 * 60 * 24 * 90 })

      const isPurchase = event === 'Purchase'
      if (['TKB', 'ZIN'].includes(clientId) && isPurchase) {
        const diagnostic = getPixelDiagnostic({ clientId, eventId, event, fbc, fbp })

        if (nuxtApp.$sentry) {
          nuxtApp.$sentry.captureMessage('[Pixel] Purchase diagnostic', {
            level: 'warning',
            tags: {
              clientId: diagnostic.clientId,
              eventId: diagnostic.eventId,
              hasFbc: diagnostic.hasFbc,
              hasFbp: diagnostic.hasFbp,
              fbcSource: diagnostic.fbcSource,
              fbpSource: diagnostic.fbpSource,
              hasAuth: diagnostic.hasAuth,
              isStandalone: diagnostic.isStandalone,
              fbpStatus: diagnostic.fbpStatus,
            },
            extra: { ...diagnostic },
          })
        }
      }

      if (!appSecret || !eventSourceUrl) return

      try {
        const result = await $request.send(commonApi.sendPixel({ appSecret, eventId, fbc, fbp, event, eventSourceUrl }))
        return result
      } catch (error) {
        console.error('[useCommon] sendPixel error:', error)
        if (nuxtApp.$sentry) {
          nuxtApp.$sentry.captureException(error, {
            level: 'error',
            extra: {
              event,
              eventId,
              clientId,
              isStandalone: ('standalone' in navigator && navigator.standalone) || window.matchMedia('(display-mode: standalone)').matches,
            },
          })
        }
        throw error
      }
    }

    const fetchFeeds = async () => {
      try {
        const result = await $request.send(commonApi.feeds({ appSecret }))
        commonStore.setBroadcastList(result.broadcastList)

        const customerMessageEnable = getSiteConfig.value?.csChat?.some((item) => item.enable && item.name === 'customermessage')
        if (!customerMessageEnable && result.mailBoxInfo) {
          result.mailBoxInfo.rows -= result.mailBoxInfo.detailRows?.CUSTOMER || 0
        }
        commonStore.setMailBoxInfo(result.mailBoxInfo)

        return result
      } catch (error) {
        console.error('[useCommon] fetchFeeds error:', error)
        throw error
      }
    }

    const fetchAnnouncements = async () => {
      try {
        const result = await $request.send(commonApi.getAnnouncements({ appSecret }))
        commonStore.setAnnouncementList(result.data)
        return result
      } catch (error) {
        console.error('[useCommon] fetchAnnouncements error:', error)
        throw error
      }
    }

    const clearAnnouncements = () => {
      commonStore.clearAnnouncementList()
    }

    const startFeedPolling = () => {
      if (!import.meta.client) return
      if (ctx.feedPollTimer) {
        clearInterval(ctx.feedPollTimer)
      }
      ctx.feedPollTimer = setInterval(async () => {
        if (!isPlaying.value) {
          await fetchFeeds()
        }
      }, FEED_POLL_INTERVAL)
    }

    const stopFeedPolling = () => {
      if (!import.meta.client) return
      if (ctx.feedPollTimer) {
        clearInterval(ctx.feedPollTimer)
        ctx.feedPollTimer = null
      }
    }

    const setNextBroadcast = () => {
      let sentList = sentNormalBroadcastList.value
      if (normalBroadcast.value) {
        sentList = [...sentNormalBroadcastList.value, normalBroadcast.value]
        // 限制陣列大小，保留最新的 MAX_BROADCAST_HISTORY 個項目
        if (sentList.length > MAX_BROADCAST_HISTORY) {
          sentList = sentList.slice(-MAX_BROADCAST_HISTORY)
        }
        sentNormalBroadcastList.value = sentList
      }

      const nextBroadcast = storeRefs.broadcastList.value?.broadcasts?.find((broadcast) => {
        return broadcast.type === '0' && !sentList.some((item) => item.broadcast_id === broadcast.broadcast_id)
      })

      normalBroadcast.value = nextBroadcast || null
    }

    const resetBroadcast = () => {
      normalBroadcast.value = null
      sentNormalBroadcastList.value = []
    }

    const startBroadcast = () => {
      if (!import.meta.client) return
      if (ctx.broadcastTimer) {
        clearInterval(ctx.broadcastTimer)
      }
      ctx.broadcastTimer = setInterval(() => {
        setNextBroadcast()
      }, 10000)
    }

    const stopBroadcast = () => {
      if (!import.meta.client) return
      if (ctx.broadcastTimer) {
        clearInterval(ctx.broadcastTimer)
        ctx.broadcastTimer = null
      }
      resetBroadcast()
    }

    const logout = () => {
      isLogin.value = false
      commonStore.clearBroadcastList()
      commonStore.clearMailBoxInfo()
      stopFeedPolling()
    }

    const startGameSession = () => {
      isPlaying.value = true
      commonStore.clearBroadcastList()
      commonStore.clearMailBoxInfo()
      stopFeedPolling()
    }

    const endGameSession = async () => {
      isPlaying.value = false
      if (isLogin.value) {
        startFeedPolling()
        await fetchFeeds()
      }
    }

    const fetchDefaultAffiliateCode = async () => {
      try {
        const result = await $request.send(commonApi.getDefaultAffiliateCode({ appSecret }))
        commonStore.setDefaultAffiliateCode(result.affiliateCode)
      } catch (error) {
        console.error('[useCommon] fetchDefaultAffiliateCode error:', error)
        throw error
      }
    }

    const initialize = async () => {
      try {
        await fetchConfig()
      } catch (error) {
        console.error('[useCommon] initialize error:', error)
      }
    }

    // Timer + watch 清理方法
    const cleanup = () => {
      // 停止所有 watch
      stopWatchers.forEach((stop) => stop?.())
      // 清理 timer
      if (ctx.feedPollTimer) {
        clearInterval(ctx.feedPollTimer)
        ctx.feedPollTimer = null
      }
      if (ctx.broadcastTimer) {
        clearInterval(ctx.broadcastTimer)
        ctx.broadcastTimer = null
      }
    }

    nuxtApp._commonContext = {
      // State
      userAgent,
      isMobile,
      announcementList: storeRefs.announcementList,
      broadcastList: storeRefs.broadcastList,
      mailBoxInfo: storeRefs.mailBoxInfo,
      normalBroadcast,
      isPlaying,
      isLogin,
      pix,
      kpix,
      defaultAffiliateCode: storeRefs.defaultAffiliateCode,

      // Methods
      initialize,
      fetchConfig,
      fetchFeeds,
      startFeedPolling,
      stopFeedPolling,
      fetchAnnouncements,
      clearAnnouncements,
      setUA,
      setPixel,
      setKPixel,
      logout,
      startGameSession,
      endGameSession,
      startBroadcast,
      stopBroadcast,
      resetBroadcast,
      fetchDefaultAffiliateCode,
      fetchSendPixel,
      cleanup,
      stopWatchers,
      // Internal (for client-side watch)
      _isLogin: isLogin,
      _clientWatchCreated: false,
    }
  }

  const ctx = nuxtApp._commonContext

  // 客戶端專屬：同步 isLogin 狀態
  // 每次 useCommon() 呼叫時直接同步，確保狀態一致
  if (import.meta.client) {
    const { isLoggedIn } = useAuthToken()
    // 直接同步 isLogin 狀態
    ctx._isLogin.value = isLoggedIn.value
    if (isLoggedIn.value) {
      ctx.startFeedPolling()
      ctx.fetchFeeds()
    }

    // 創建 watch 監聽後續變化（如果還沒創建）
    if (!ctx._clientWatchCreated) {
      ctx.stopWatchers.push(
        watch(isLoggedIn, (value) => {
          ctx._isLogin.value = value
          if (value) {
            ctx.startFeedPolling()
            ctx.fetchFeeds()
          }
        })
      )
      ctx._clientWatchCreated = true
    }
  }

  // 元件卸載時自動清理 timer（每次調用都需要檢查）
  if (getCurrentInstance()) {
    onBeforeUnmount(() => {
      ctx.stopFeedPolling()
      ctx.stopBroadcast()
    })
  }

  return ctx
}
